import axios, { type AxiosInstance } from 'axios';
import { msalInstance } from '@/lib/msal';
import { runtimeConfig } from '@/runtimeConfig';

let apiClient: AxiosInstance | null = null;

export function configureApiClient(instance: AxiosInstance) {
  apiClient = instance;
}

function withApiVersion(baseUrl: string): string {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return normalizedBase.endsWith('/v1')
    ? normalizedBase
    : `${normalizedBase}/v1`;
}

async function tryAcquireApiToken(): Promise<string | null> {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account) {
    return null;
  }

  const scopes = runtimeConfig.MsalScopes.Or(
    `api://${runtimeConfig.MsalClientId.Or('default')}/.default`,
  )
    .split(/[\s,]+/)
    .map((scope) => scope.trim())
    .filter(Boolean);

  if (scopes.length === 0) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      account,
      scopes,
    });
    return response.accessToken;
  } catch {
    return null;
  }
}

export function getApiClient(): AxiosInstance {
  if (apiClient) {
    return apiClient;
  }

  apiClient = axios.create({
    baseURL: withApiVersion(runtimeConfig.ApiBaseUrl.Or('/api')),
  });

  apiClient.interceptors.request.use(async (config) => {
    const token = await tryAcquireApiToken();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return apiClient;
}

export const NO_ACCESS_MESSAGE =
  "You don't have permission to access this resource.";

export type ApiErrorKind =
  | 'forbidden'
  | 'not-found'
  | 'timeout'
  | 'unavailable';

export function classifyApiError(error: unknown): ApiErrorKind {
  if (axios.isAxiosError(error)) {
    switch (error.response?.status) {
      case 403:
        return 'forbidden';
      case 404:
        return 'not-found';
      case 408:
      case 504:
        console.error('API request timed out', error);
        return 'timeout';
    }
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      console.error('API request timed out', error);
      return 'timeout';
    }
  }

  console.error('API request failed', error);
  return 'unavailable';
}
